<script lang="ts" setup>
import { useToast } from '@afo/bsf-core'
import FormLayout from '@/components/Formv2/layouts/Form/Form.vue'
import Step from '@/components/Step/Step.vue'
import StepController from '@/components/Step/StepController/StepController.vue'
import Steps from '@/components/Step/Steps.vue'
import useMultiSportPartnerApi from '@/domains/partners/multisport/api/useMultisportPartnersApi'
import FormFooter from '@/domains/partners/multisport/components/Form/FormFooter.vue'
import FormInfo from '@/domains/partners/multisport/components/Form/FormInfo.vue'
import { useMultiSportPartner } from '@/domains/partners/multisport/composables/usePartner'
import { useSteps } from '@/domains/partners/multisport/composables/useSteps'
import { useMultisportPartnersStore } from '@/domains/partners/multisport/store/useMultiSportPartnersStore'
import FormStepOne from '@/domains/partners/multisport/components/Steps/StepOne/StepOne.vue'
import FormStepTwo from '@/domains/partners/multisport/components/Steps/StepTwo/StepTwo.vue'
import FormStepThree from '@/domains/partners/multisport/components/Steps/StepThree/StepThree.vue'
import FormStepFour from '@/domains/partners/multisport/components/Steps/StepFour/StepFour.vue'
import { useRecaptcha } from '~/domains/recaptcha/composables/useRecaptcha'

const { recaptchaValid, loadCaptcha } = useRecaptcha()
const submitted = ref(false)
const {
  steps,
  direction,
  activeIndex,
  getActiveStep,
  getConfigForNextStep,
  getConfigForPreviousStep,
  nextClick,
  previousClick,
} = useSteps(recaptchaValid, submitted)
const { isStepValid } = useMultiSportPartner()
const { isDesktop } = useBSBreakpoints()
const { form, parseFormPayload, resetForm } = useMultisportPartnersStore()
const { createMultisportPartner } = useMultiSportPartnerApi()
const toast = useToast()
const { t } = useI18n()

const stepComponents = {
  1: FormStepOne,
  2: FormStepTwo,
  3: FormStepThree,
  4: FormStepFour,
}

const currentStepComponent = shallowRef(stepComponents[(activeIndex.value + 1) as keyof typeof stepComponents])

watch(() => activeIndex.value, async (val) => {
  // load captcha Script
  if (val === 2) {
    await loadCaptcha()
  }
})

const validStep = async () => {
  const stepNumber = activeIndex.value + 1
  const isValid = await isStepValid(stepNumber)
  if (!isValid) return

  if (stepNumber === 3) {
    if (recaptchaValid.value) return

    const payload = parseFormPayload(form)

    try {
      submitted.value = true
      await createMultisportPartner(payload)
      toast.success(t('partners.api.success'))
      resetForm()
      nextClick()
    } catch {
      toast.error(t('partners.api.error'))
      submitted.value = false
    }
  } else {
    nextClick()
  }
}

watch(activeIndex, (newValue) => {
  currentStepComponent.value = stepComponents[(newValue + 1) as keyof typeof stepComponents]
})
</script>

<template>
  <div class="partnersMultisport">
    <div class="partnersMultisport__header">
      <h1 class="partnersMultisport__header-title">
        {{ $t('partners.title') }}
      </h1>

      <p class="partnersMultisport__header-description">
        {{ $t('partners.description') }}
      </p>
    </div>

    <div class="partnersMultisport__formContainer">
      <div class="partnersMultisport__leftColumn">
        <FormInfo />
      </div>

      <div class="partnersMultisport__rightColumn">
        <div class="partnersMultisport__stepsWrapper">
          <div
            v-if="isDesktop"
            class="partnersMultisport__stepsContainer"
          >
            <Steps :steps="steps" />
          </div>

          <Step
            v-else-if="getActiveStep"
            v-bind="getActiveStep"
          />
        </div>

        <FormLayout>
          <Transition
            :name="direction === 'right' ? 'slideRight' : 'slideLeft'"
            mode="out-in"
          >
            <keep-alive>
              <component
                :is="currentStepComponent"
                :key="activeIndex"
              />
            </keep-alive>
          </Transition>

          <StepController
            v-if="activeIndex !== 3"
            :previous-button-config="getConfigForPreviousStep"
            :next-button-config="getConfigForNextStep"
            @previous-step="previousClick"
            @next-step="validStep"
          />

          <FormFooter />
        </FormLayout>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.partnersMultisport {
  margin: 0 2.4rem;
  padding: 3.2rem 3rem;
  background-color: $gray-100;
  border-radius: 1.6rem;

  &__header {
    margin-bottom: 3.2rem;

    &-title {
      @include h6;
      margin-bottom: 3.2rem;
      color: $text-400;
      text-align: center;
    }

    &-description {
      @include body-3-medium;
      color: $text-400;
      text-align: center;
    }
  }

  &__formContainer {
    display: flex;
    flex-direction: column-reverse;
    margin-top: 3.2rem;
  }

  &__stepsWrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1.6rem;
    margin-bottom: 3.2rem;
    text-align: center;
  }

  &__stepsContainer {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 0.8rem;
  }

  @include atL {
    padding: 6.4rem;

    &__header {
      max-width: 100rem;
      margin: auto;
      padding: 0 0 6.4rem;
    }

    &__stepsWrapper {
      flex-direction: row;
      justify-content: space-around;
      gap: 5rem;
    }

    &__formContainer {
      display: flex;
      flex-direction: row;
      gap: 6rem;
      max-width: 152rem;
      margin: auto;
    }

    &__rightColumn,
    &__leftColumn {
      flex-basis: 50%;
      width: 50%;
    }
  }
}
</style>
